// Home Care pillar — maintenance & prevention articles. The research-phase content most
// visitors actually want ("how do I take care of my house") before they ever book a job.
// Same data-driven pattern as data.js; richer schema: readTime, takeaways[], checklists[]
// (items = [task, cadence, 'diy'|'pro']), sections[].tip renders as a Pro tip callout.
// Prices are typical 2026 U.S. ranges for guidance only — actual prices vary by city and job.

export const HOME_CARE_PAGES = {
  'home-maintenance-checklist': {
    service: 'Home maintenance',
    category: 'Home care',
    featured: true,
    title: 'The Complete Seasonal Home Maintenance Checklist (2026) | BestHand',
    metaDescription:
      'The full season-by-season home maintenance checklist — what to do in spring, summer, fall and winter, what each task prevents, and which jobs to leave to a pro.',
    h1: 'The complete seasonal home maintenance checklist',
    updated: 'August 2026',
    readTime: 9,
    intro:
      'A house does not fail all at once — it fails a little at a time, through small tasks nobody did. This is the full season-by-season checklist we recommend to every homeowner: what to do, when to do it, what each task prevents, and which jobs are worth handing to a pro.',
    takeaways: [
      'Most expensive home repairs start as a skipped 15-minute maintenance task.',
      'Spring and fall are the two heavy seasons — book pros a few weeks ahead of the rush.',
      'A consistent routine costs a few hundred dollars a year and protects your single largest asset.',
      'DIY the simple tasks; bring in a pro for anything involving heights, gas, or your HVAC system.',
    ],
    checklists: [
      {
        title: '🌸 Spring — recover from winter',
        note: 'Winter is hardest on the outside of your home. Spring is when you find the damage while it is still small.',
        items: [
          ['Inspect the roof for lifted or missing shingles', 'Every spring', 'pro'],
          ['Clean gutters and check downspout drainage', 'Spring & fall', 'pro'],
          ['Service the A/C before the first heat wave', 'Every spring', 'pro'],
          ['Check exterior caulking around windows and doors', 'Every spring', 'diy'],
          ['Inspect the foundation for new cracks', 'Every spring', 'diy'],
          ['Test outdoor faucets and irrigation for freeze damage', 'Every spring', 'diy'],
        ],
      },
      {
        title: '☀️ Summer — the light season',
        note: 'Long days and dry weather make summer the best time for exterior projects and touch-ups.',
        items: [
          ['Wash siding and touch up exterior paint', 'Every summer', 'diy'],
          ['Pressure-wash the deck, patio and walkways', 'Every summer', 'pro'],
          ['Re-seal or stain the deck', 'Every 2–3 years', 'pro'],
          ['Check and lubricate garage door hardware', 'Twice a year', 'diy'],
          ['Trim trees and shrubs away from the house', 'Every summer', 'pro'],
        ],
      },
      {
        title: '🍂 Fall — prepare for the cold',
        note: 'The tasks with the highest stakes. Everything on this list prevents a specific winter emergency.',
        items: [
          ['Service the furnace or heating system', 'Every fall', 'pro'],
          ['Clean gutters after the leaves drop', 'Spring & fall', 'pro'],
          ['Shut off and drain outdoor faucets', 'Every fall', 'diy'],
          ['Seal air leaks around windows and doors', 'Every fall', 'diy'],
          ['Check roof and flashing before snow', 'Every fall', 'pro'],
          ['Clean the dryer vent line', 'Every year', 'pro'],
          ['Test sump pump before the wet season', 'Every fall', 'diy'],
        ],
      },
      {
        title: '❄️ Winter — monitor and protect',
        note: 'Less doing, more watching. Catch the small signals before they become water damage.',
        items: [
          ['Replace furnace filter', 'Every 1–3 months', 'diy'],
          ['Watch for ice dams at the roof edge', 'After heavy snow', 'diy'],
          ['Keep interior doors open so pipes stay warm', 'Cold snaps', 'diy'],
          ['Test smoke and CO detectors', 'Twice a year', 'diy'],
          ['Check the attic for condensation or frost', 'Mid-winter', 'diy'],
        ],
      },
    ],
    sections: [
      {
        h2: 'Why a checklist beats reacting',
        body: [
          'Reactive repairs are the most expensive kind: an emergency plumber costs multiples of a scheduled visit, and the damage a failure causes usually costs more than the failure itself. A burst supply line is a $150 fix — the flooded floor below it is a $3,000–$8,000 claim.',
          'Working from a seasonal list flips that. You find problems when they are cheap and boring, you book pros when their calendars are open instead of during the first cold snap, and nothing depends on remembering.',
        ],
        tip: 'Put two 30-minute "walk the house" appointments in your calendar — one in April, one in October. The checklist does the rest.',
      },
      {
        h2: 'What to DIY and what to book',
        body: [
          'A good rule: if the task involves a ladder above one story, gas, refrigerant, or your main electrical panel, book a pro. The savings from doing it yourself are small compared to the risk, and pros spot adjacent problems you would miss — a gutter cleaner who notices lifted flashing just saved you a ceiling.',
          'Everything at ground level with hand tools — caulking, filters, testing detectors, draining faucets — is genuinely DIY-friendly, and this checklist marks each task accordingly.',
        ],
      },
      {
        h2: 'What a year of maintenance actually costs',
        body: [
          'For a typical single-family home, the professional tasks on this list — two gutter cleanings, an A/C tune-up, a furnace tune-up, a dryer vent cleaning and occasional small repairs — generally land in the $600–$1,200 per year range depending on your area.',
          'Compare that to what the list prevents: water damage from clogged gutters, a mid-January furnace failure, a dryer fire, frozen pipes. Maintenance is the cheapest insurance a homeowner can buy.',
        ],
      },
    ],
    faqs: [
      ['How often should I do home maintenance?', 'Do a focused pass in spring and fall, with a handful of monthly habits like changing HVAC filters. The two seasonal passes catch most problems while they are small.'],
      ['What home maintenance is most important?', 'The tasks that prevent water and fire damage: gutter cleaning, roof checks, heating service, and dryer vent cleaning. Water intrusion and dryer lint fires cause some of the most expensive avoidable claims.'],
      ['Can I do home maintenance myself?', 'Much of it, yes — filters, caulking, draining outdoor faucets, testing detectors. Book a pro for roofs, gutters above one story, HVAC service, and anything involving gas.'],
      ['How much should I budget for home maintenance?', 'A common rule of thumb is 1% of your home’s value per year for maintenance and repairs combined. The scheduled-maintenance portion is usually $600–$1,200 for a typical house.'],
    ],
    related: ['fall-home-maintenance-checklist', '10-small-problems-big-repairs', 'new-homeowner-first-year'],
  },

  'fall-home-maintenance-checklist': {
    service: 'Fall maintenance',
    category: 'Home care',
    title: 'Fall Home Maintenance: 12 Tasks To Do Before the First Freeze | BestHand',
    metaDescription:
      'The 12 fall maintenance tasks that matter most — gutters, furnace, pipes, roof and more — what each prevents, what it costs, and when to schedule it.',
    h1: 'Fall home maintenance: 12 tasks before the first freeze',
    updated: 'August 2026',
    readTime: 7,
    intro:
      'Fall is the one season where skipping maintenance has an immediate price: everything on this list prevents a specific winter emergency, from frozen pipes to a furnace that quits on the first freezing night. Here are the 12 tasks that matter, in the order to do them.',
    takeaways: [
      'Book your furnace tune-up in September — HVAC calendars fill up by the first cold week.',
      'Gutters matter twice: clear them after the leaves drop, before the first hard freeze.',
      'Ten minutes spent draining outdoor faucets prevents the most common winter pipe burst.',
      'Air-sealing windows and doors pays for itself in one season of heating bills.',
    ],
    checklists: [
      {
        title: 'The 12 tasks, in order',
        note: 'Work top to bottom: heating first (books out fastest), then water, then air.',
        items: [
          ['Furnace / heating tune-up', 'September', 'pro'],
          ['Replace furnace filter', 'Now, then every 1–3 months', 'diy'],
          ['Clean gutters & downspouts after leaf drop', 'Late fall', 'pro'],
          ['Inspect roof shingles and flashing', 'Before snow', 'pro'],
          ['Shut off + drain outdoor faucets & hoses', 'Before first freeze', 'diy'],
          ['Winterize irrigation / sprinkler system', 'Before first freeze', 'pro'],
          ['Caulk & weatherstrip windows and doors', 'October', 'diy'],
          ['Clean the dryer vent line', 'Annually — fall is ideal', 'pro'],
          ['Test the sump pump', 'Before the wet season', 'diy'],
          ['Reverse ceiling fans to winter direction', 'When heat comes on', 'diy'],
          ['Test smoke & CO detectors, swap batteries', 'Twice a year', 'diy'],
          ['Stack: chimney sweep if you burn wood', 'Before first fire', 'pro'],
        ],
      },
    ],
    sections: [
      {
        h2: 'Why September beats November',
        body: [
          'Every HVAC company has the same calendar: quiet in September, slammed the week of the first freeze. Booking your tune-up early means you choose the time, the pro is not rushing, and if the tune-up finds a real problem — a cracked heat exchanger, a dying blower — you have weeks to fix it instead of days without heat.',
          'The same logic applies to gutter and roof work. Roofers stop taking small jobs once the emergency-repair season starts.',
        ],
        tip: 'The cheapest week of the year to book heating service is usually mid-September. The most expensive is the first freezing week.',
      },
      {
        h2: 'The two tasks people regret skipping',
        body: [
          'Frozen hose bibs are the classic. Water left in an outdoor faucet or an attached hose freezes, expands, and splits the pipe inside your wall — you find out in spring, after weeks of slow leaking. Draining them takes ten minutes and a bucket.',
          'The second is gutters. Clogged gutters hold water against your roof edge; when it freezes it forms ice dams that push meltwater up under the shingles and into your ceiling. One cleaning after leaf drop prevents the whole chain.',
        ],
      },
      {
        h2: 'What fall maintenance costs',
        body: [
          'Typical 2026 ranges: furnace tune-up $80–$200, gutter cleaning $100–$250, dryer vent cleaning $90–$180, irrigation winterization $60–$150, chimney sweep $130–$300. The DIY tasks cost a tube of caulk and an afternoon.',
          'The full professional slate for an average house runs $400–$900 — roughly the deductible on a single water-damage claim.',
        ],
      },
    ],
    faqs: [
      ['When should I start fall home maintenance?', 'Start in September with heating service, and finish the water-related tasks (gutters, faucets, irrigation) before your area’s first hard freeze.'],
      ['Do I really need a furnace tune-up every year?', 'An annual tune-up keeps efficiency up, catches safety issues like cracked heat exchangers, and is required by many manufacturer warranties. It is the single most-skipped task with the worst failure timing.'],
      ['What temperature do pipes freeze at?', 'Exposed pipes can freeze when temperatures drop below about 20°F (-6°C) for several hours — sooner for outdoor faucets with hoses still attached. Drain and disconnect before the first freeze.'],
      ['Is fall or spring gutter cleaning more important?', 'Fall, if you only do one — clogged gutters plus freezing temperatures cause ice dams and roof-edge damage. Ideally do both.'],
    ],
    related: ['home-maintenance-checklist', 'gutter-cleaning-maintenance', 'hvac-maintenance-guide'],
  },

  '10-small-problems-big-repairs': {
    service: 'Home repairs',
    category: 'Home care',
    title: '10 Small Home Problems That Become Expensive Repairs | BestHand',
    metaDescription:
      'A dripping faucet, a cracked tile, a sticking door — 10 small home problems that quietly grow into $1,000+ repairs, and what fixing each one early costs.',
    h1: '10 small home problems that become expensive repairs',
    updated: 'August 2026',
    readTime: 8,
    intro:
      'Almost every four-figure home repair started life as a two-figure one. These are the ten small problems pros see most often — what each one costs to fix today, what it grows into if you wait, and how to tell when the clock is running.',
    costTable: {
      caption: 'Fix it now vs. what it becomes',
      head: ['The small problem', 'Fix it now', 'What it becomes'],
      rows: [
        ['Dripping faucet or running toilet', '$75–$200', '$500+/yr in water, damaged vanity'],
        ['Cracked bathroom caulk or grout', '$100–$300', '$2,000–$8,000 subfloor water damage'],
        ['Clogged gutters', '$100–$250', '$1,500–$10,000 roof-edge & foundation damage'],
        ['Small roof leak / lifted shingle', '$150–$500', '$3,000–$12,000 decking, insulation & ceiling'],
        ['Sticking door or window', '$100–$250', 'Can signal foundation movement — $5,000+'],
        ['Peeling exterior paint', '$200–$600 (spot)', '$3,000+ siding rot repair'],
        ['Lint-clogged dryer vent', '$90–$180', 'House-fire risk + dead dryer heating element'],
        ['Slow drain', '$100–$275', 'Full line blockage, sewage backup'],
        ['Water stain on ceiling', '$150–$400 (find & fix)', 'Mold remediation $2,000–$10,000'],
        ['Loose deck railing or step', '$100–$300', 'Injury liability + full rebuild'],
      ],
    },
    sections: [
      {
        h2: 'Why small problems compound',
        body: [
          'Houses fail through chains, not single events. Water is the usual driver: a hairline gap in caulk lets a few drops through each shower; the drops soak the subfloor; the subfloor grows mold and rots; a $150 recaulking job turns into a torn-out bathroom floor. Every line in the table above is a chain like that.',
          'The other compounding force is season. A lifted shingle in August is a quick fix. The same shingle in January, under snow, after a thaw-freeze cycle, is a ceiling repair. Timing is why fall maintenance matters so much.',
        ],
        tip: 'The cheapest moment to fix anything is the day you first notice it. The second cheapest is today.',
      },
      {
        h2: 'The three signals to never ignore',
        body: [
          'Water where it should not be — a stain, a musty smell, a warped board. Water damage is always cheaper the week you find it than the month after.',
          'Doors and windows that suddenly stick. Wood swells seasonally, but a door that sticks year-round or a new diagonal crack above a frame can mean the structure is moving.',
          'Anything electrical that is warm, buzzing, or flickering. Unlike plumbing, electrical faults do not announce themselves gradually — have them looked at immediately.',
        ],
      },
      {
        h2: 'A one-hour audit twice a year',
        body: [
          'Walk the house with this list in spring and fall: run every faucet and flush every toilet, look under every sink, scan every ceiling, open every window, walk the perimeter looking at paint, caulk and gutters. One hour, twice a year, catches nearly everything on this page at its cheap stage.',
          'Found something? Bundle it. A handyman fixing four small items in one visit costs far less per item than four separate calls — you pay one trip charge instead of four.',
        ],
      },
    ],
    faqs: [
      ['What home repairs should I never delay?', 'Anything involving active water (leaks, stains, drips), electrical warmth or buzzing, and structural movement (new cracks, sticking doors). These three categories compound fastest.'],
      ['Is a dripping faucet really worth fixing?', 'Yes — beyond the $75–$200 fix, a steady drip wastes up to 3,000 gallons a year, and the constant moisture damages the fixture and cabinet below.'],
      ['How do I know if a wall crack is serious?', 'Hairline vertical cracks in drywall are usually settling. Diagonal cracks from door/window corners, stair-step cracks in brick, or cracks that keep growing deserve a professional look.'],
      ['Should I bundle small repairs into one visit?', 'Always. Most pros have a minimum charge or trip fee, so a punch-list visit fixing several items is the best value per repair.'],
    ],
    related: ['home-maintenance-checklist', 'caulking-grout-guide', 'gutter-cleaning-maintenance'],
  },

  'gutter-cleaning-maintenance': {
    service: 'Gutter cleaning',
    category: 'Home care',
    title: 'Gutter Cleaning: Why It Matters, When To Do It & What It Costs | BestHand',
    metaDescription:
      'What clogged gutters actually do to a house, how often to clean them, what gutter cleaning costs in 2026, and when gutter guards are worth it.',
    h1: 'Gutter cleaning: the least glamorous job that protects your whole house',
    updated: 'August 2026',
    readTime: 6,
    intro:
      'Gutters have one job: move roof water away from your house. When they clog, that water goes everywhere you do not want it — under shingles, behind fascia boards, and down into your foundation. Here is what gutter care actually prevents, when to schedule it, and what it costs.',
    takeaways: [
      'Clean twice a year — late spring and after the last leaves drop in fall.',
      'Clogged gutters are a top cause of both roof-edge rot and basement water problems.',
      'Professional cleaning for a typical home runs $100–$250 — ladder work is the reason to hire it out.',
      'Gutter guards reduce cleanings but do not eliminate them.',
    ],
    sections: [
      {
        h2: 'What clogged gutters actually damage',
        body: [
          'Up top: water backing up in a clogged gutter soaks the fascia board and roof edge, rotting the wood that your gutters are attached to. In winter, that standing water becomes the base of ice dams that force meltwater under the shingles and into the ceiling below.',
          'Down below: a clogged downspout dumps hundreds of gallons of roof water right at your foundation instead of away from it. Over seasons, that is how basements get damp, how hairline foundation cracks widen, and how landscaping erodes.',
        ],
      },
      {
        h2: 'When and how often',
        body: [
          'Twice a year is the standard: once in late spring (seed pods, blossoms, storm debris) and once in late fall, after the last leaves are down. Homes under mature trees may need a third pass; homes with no overhanging trees can sometimes stretch to annual.',
          'Add an inspection after any major storm — one branch is enough to block a downspout, and you will not see it from the ground.',
        ],
        tip: 'A quick self-check without a ladder: during heavy rain, walk the perimeter. Water sheeting over a gutter edge means a clog right there.',
      },
      {
        h2: 'DIY or pro?',
        body: [
          'The task is simple; the ladder is the risk. Falls from ladders are one of the most common serious home-DIY injuries, and gutter work means moving the ladder every few feet for an hour. For anything above a single story, hiring it out is the sensible call.',
          'A pro cleaning typically costs $100–$250 for an average home, takes under an hour, and includes flushing downspouts — the part DIY cleanings usually skip. Many pros will flag roof-edge issues they see up close, which is a free bonus inspection.',
        ],
      },
      {
        h2: 'Are gutter guards worth it?',
        body: [
          'Quality guards cut cleaning frequency dramatically, and they make sense under heavy tree cover. They do not eliminate maintenance — fine debris still gets through and the guards themselves need occasional clearing — and cheap screens can make cleaning harder.',
          'The honest math: guards are a convenience purchase that pays off in reduced ladder trips, not a set-and-forget solution.',
        ],
      },
    ],
    faqs: [
      ['How often should gutters be cleaned?', 'Twice a year for most homes — late spring and late fall. More often under heavy tree cover.'],
      ['What happens if I never clean my gutters?', 'Water overflows against the roof edge and foundation: rotted fascia, ice dams, damp basements and eroded grading are the typical results, each far costlier than years of cleanings.'],
      ['How much does gutter cleaning cost in 2026?', 'Typically $100–$250 for a single-family home, depending on size, height and how clogged the system is. Downspout flushing should be included.'],
      ['Do gutter guards mean I never clean gutters again?', 'No — they reduce frequency significantly but fine debris still accumulates, and the guards need occasional clearing themselves.'],
    ],
    related: ['fall-home-maintenance-checklist', '10-small-problems-big-repairs', 'home-maintenance-checklist'],
  },

  'hvac-maintenance-guide': {
    service: 'HVAC',
    category: 'Home care',
    title: 'HVAC Maintenance 101: Filters, Tune-Ups & Lower Bills | BestHand',
    metaDescription:
      'How to take care of your heating and cooling system: filter schedules, what a professional tune-up includes, what it costs, and how maintenance cuts energy bills.',
    h1: 'HVAC maintenance 101: the guide to filters, tune-ups and lower bills',
    updated: 'August 2026',
    readTime: 7,
    intro:
      'Your heating and cooling system is the most expensive machine in your house and the biggest line on your energy bill. It is also the system where basic maintenance pays back fastest — in efficiency, in lifespan, and in not losing heat on the coldest night of the year.',
    takeaways: [
      'The filter is 80% of DIY HVAC care — check monthly, change every 1–3 months.',
      'One professional tune-up per system per year: A/C in spring, furnace in fall.',
      'A neglected system loses roughly 5% efficiency per year and dies years earlier.',
      'Warm rooms, weak airflow, new noises and rising bills are the four early warning signs.',
    ],
    costTable: {
      caption: 'Typical HVAC maintenance costs (2026, U.S.)',
      head: ['Service', 'Typical range', 'How often'],
      rows: [
        ['Furnace filter (DIY)', '$5–$30', 'Every 1–3 months'],
        ['A/C tune-up', '$80–$200', 'Every spring'],
        ['Furnace tune-up', '$80–$200', 'Every fall'],
        ['Duct cleaning', '$300–$700', 'Every 3–5 years, if needed'],
        ['Refrigerant recharge (if leaking)', '$200–$600', 'Repair — find the leak too'],
      ],
    },
    sections: [
      {
        h2: 'The filter: small, cheap, decisive',
        body: [
          'A clogged filter starves the system of airflow. The blower works harder, coils ice over or overheat, efficiency drops, and parts wear out early. It is the root cause behind a remarkable share of service calls.',
          'Check it monthly — hold it up to a light; if you cannot see light through it, change it. Households with pets or allergies should lean toward monthly changes; a rarely-used guest system can stretch to three months.',
        ],
        tip: 'Write the install date on the filter frame with a marker. Future you will never have to guess.',
      },
      {
        h2: 'What a professional tune-up actually includes',
        body: [
          'A real tune-up is inspection plus cleaning plus measurement: coils cleaned, refrigerant pressure checked, electrical connections tightened, condensate drain cleared, burners inspected, heat exchanger checked for cracks (a genuine safety item — this is how carbon monoxide risks get caught), and temperature differentials measured against spec.',
          'Twenty minutes of "sprayed some coil cleaner" is not a tune-up. Ask for the checklist — reputable pros are happy to show what was measured.',
        ],
      },
      {
        h2: 'The payback math',
        body: [
          'Regular maintenance keeps a system near its rated efficiency; neglect costs roughly 5% efficiency per year as coils foul and airflow drops. On a $2,400 annual heating-and-cooling spend, that is real money every year.',
          'Lifespan is the bigger number: well-maintained systems commonly reach 15–20 years, neglected ones often fail at 10–12. Pushing a $8,000–$15,000 replacement five years further out is worth a decade of $150 tune-ups many times over.',
        ],
      },
      {
        h2: 'The four early warning signs',
        body: [
          'Rooms that used to be comfortable running warm or cold; airflow from registers noticeably weaker; new noises — grinding, squealing, repeated clicking; and energy bills creeping up without a rate change. Any one of them is a reason to book a check before peak season.',
          'What they buy you is time: almost every catastrophic HVAC failure telegraphs itself for weeks first.',
        ],
      },
    ],
    faqs: [
      ['How often should HVAC filters be changed?', 'Check monthly, change every 1–3 months. Pets, allergies and heavy use push toward monthly.'],
      ['Is an annual HVAC tune-up worth it?', 'Yes — it maintains efficiency, catches safety issues like cracked heat exchangers, preserves most manufacturer warranties, and extends system life by years.'],
      ['When is the best time to service A/C and heating?', 'A/C in spring before the first heat wave, heating in early fall before the first freeze — both to beat the seasonal rush.'],
      ['Why is my HVAC bill going up?', 'The usual suspects are a clogged filter, fouled coils, low refrigerant or duct leaks — all maintenance items. If bills climb without a rate change, book an inspection.'],
    ],
    related: ['fall-home-maintenance-checklist', 'home-maintenance-checklist', '10-small-problems-big-repairs'],
  },

  'water-heater-maintenance': {
    service: 'Water heater',
    category: 'Home care',
    title: 'Water Heater Maintenance: Make Yours Last 15+ Years | BestHand',
    metaDescription:
      'The three maintenance tasks that double a water heater’s life — annual flush, anode rod checks, T&P valve test — plus the warning signs of a failing tank.',
    h1: 'Water heater maintenance: how to make yours last 15+ years',
    updated: 'August 2026',
    readTime: 6,
    intro:
      'Most tank water heaters die at 8–12 years — and most of those deaths were preventable. The killer is sediment and corrosion, and the defense is three simple tasks almost nobody does. Here is how to double the life of a $1,500+ appliance.',
    takeaways: [
      'An annual tank flush removes the sediment that destroys efficiency and steel.',
      'The anode rod is a $30–$60 part that exists to corrode so your tank does not — check it every 2–3 years.',
      'Rumbling sounds, rusty hot water and moisture at the base are the three failure warnings.',
      'A tank that fails catastrophically does not just die — it floods whatever is below it.',
    ],
    sections: [
      {
        h2: 'The annual flush',
        body: [
          'Minerals in your water settle to the tank bottom as sediment. On a gas heater, that layer sits between the burner and the water — the burner overheats the steel to compensate, wasting fuel and fatiguing the tank. The popping and rumbling an old heater makes is literally water boiling through sediment.',
          'A flush — draining the tank through its valve until the water runs clear — removes the layer. It is a 30–45 minute job a comfortable DIYer can do, or a quick, inexpensive visit for a plumber who will also eyeball the rest of the unit.',
        ],
      },
      {
        h2: 'The anode rod: the part designed to die',
        body: [
          'Inside the tank hangs a magnesium or aluminum rod whose only purpose is to corrode first, sparing the steel around it. Once the rod is consumed — typically 3–5 years — the tank itself starts rusting, and from that point the countdown is running.',
          'Checking it every 2–3 years and swapping it when it is mostly gone is the single highest-leverage maintenance act on a water heater: a $30–$60 part standing between you and a $1,200–$2,500 replacement.',
        ],
        tip: 'If your hot water has started smelling like rotten eggs, that is often the anode rod reacting with your water — a replacement rod in a different alloy usually solves it.',
      },
      {
        h2: 'Reading the warning signs',
        body: [
          'Rumbling or popping means heavy sediment. Rusty water from the hot tap only means the tank interior is corroding. Moisture, drips or rust streaks at the tank base mean the steel is already seeping — replace the unit before it fails outright, on your schedule instead of the tank’s.',
          'A catastrophic tank failure releases 40–80 gallons immediately and then keeps flowing until someone shuts the supply. If your heater lives above a finished space, the water damage typically costs several times the heater.',
        ],
      },
    ],
    faqs: [
      ['How often should a water heater be flushed?', 'Once a year for most homes; twice in hard-water areas. If it has never been flushed and is over 5 years old, have a plumber do the first one.'],
      ['How long should a water heater last?', 'Tank units: 8–12 years neglected, 15+ with flushes and anode changes. Tankless units commonly reach 20 years with annual descaling.'],
      ['When should I replace instead of repair?', 'Any sign of tank corrosion — rusty hot water, moisture at the base — means replacement; the tank itself cannot be repaired. Component failures (thermostat, element, valve) on a younger tank are worth fixing.'],
      ['What temperature should a water heater be set to?', '120°F (49°C) is the standard recommendation — hot enough for the house, low enough to slow scalding risk, energy waste and tank stress.'],
    ],
    related: ['home-maintenance-checklist', '10-small-problems-big-repairs', 'hvac-maintenance-guide'],
  },

  'dryer-vent-cleaning-safety': {
    service: 'Dryer vent cleaning',
    category: 'Home care',
    title: 'Dryer Vent Cleaning: The Fire-Safety Task Everyone Skips | BestHand',
    metaDescription:
      'Lint-clogged dryer vents cause thousands of house fires a year. How to tell if yours is clogged, how often to clean it, and what professional cleaning costs.',
    h1: 'Dryer vent cleaning: the fire-safety chore everyone skips',
    updated: 'August 2026',
    readTime: 5,
    intro:
      'The lint trap catches only part of what a dryer produces. The rest travels down the vent line toward the outside — and lint is close to ideal fire fuel, packed into a warm tube next to a heating element. Clogged dryer vents are behind thousands of U.S. house fires every year, and the fix is a once-a-year cleaning.',
    takeaways: [
      'Clean the trap every load; have the vent LINE cleaned once a year.',
      'Clothes taking two cycles to dry is the #1 clog symptom — and it doubles your energy cost per load.',
      'A professional line cleaning runs $90–$180 and takes under an hour.',
      'Flexible foil duct sagging behind the dryer is where lint collects most — rigid metal duct is the safe upgrade.',
    ],
    sections: [
      {
        h2: 'How to tell your vent is clogging',
        body: [
          'The signs arrive in order: loads take longer than one cycle; the dryer top and the laundry room get noticeably hot; clothes come out hotter than usual; a burning smell appears during runs; and the outside flap barely flutters when the dryer is on. Any two of those together mean the line is restricting.',
          'The energy angle is immediate: a dryer pushing air through a clogged line works twice as long per load. A $120 cleaning frequently pays for itself on the utility bill alone within the year.',
        ],
        tip: 'Quick test: run the dryer and look at the outside vent flap. Strong, steady airflow = healthy line. A weak flutter = time to clean.',
      },
      {
        h2: 'What a professional cleaning involves',
        body: [
          'A pro disconnects the dryer, runs a rotating brush the full length of the line (often 10–25 feet with bends you cannot reach from either end), vacuums the dislodged lint, cleans the exterior hood, and checks the connection duct behind the machine — the sagging flexible section where most lint settles.',
          'While there, a good tech will flag the two common hazards: crushed duct behind a dryer pushed too close to the wall, and old white vinyl duct, which is combustible and no longer code — both cheap fixes worth doing on the spot.',
        ],
      },
      {
        h2: 'Between cleanings',
        body: [
          'Clean the lint trap every single load — a full trap pushes more lint into the line. Every few months, vacuum around and under the machine and behind it if you can reach. Never run the dryer while away from home or asleep; if a lint fire starts, minutes matter.',
          'If your vent line is unusually long, has multiple bends, or serves a heavy-use household, move to a twice-a-year schedule.',
        ],
      },
    ],
    faqs: [
      ['How often should dryer vents be cleaned?', 'The full vent line once a year for typical use — twice for long lines or heavy use. The lint trap: every load.'],
      ['What are the signs of a clogged dryer vent?', 'Longer drying times, a hot dryer top and laundry room, burning smells, and weak airflow at the outside vent flap.'],
      ['How much does dryer vent cleaning cost?', 'Typically $90–$180 in 2026, depending on line length and accessibility. Roof-terminated vents run higher.'],
      ['Can I clean a dryer vent myself?', 'Short, straight lines: yes, with a brush kit. Long lines, bends, or roof exits need a pro’s rotating brush to actually clear rather than compact the lint.'],
    ],
    related: ['fall-home-maintenance-checklist', 'home-maintenance-checklist', '10-small-problems-big-repairs'],
  },

  'caulking-grout-guide': {
    service: 'Caulking & sealing',
    category: 'Home care',
    title: 'Caulk & Grout: The $30 Habit That Prevents $3,000 Water Damage | BestHand',
    metaDescription:
      'Failed caulk and grout are the #1 source of hidden bathroom water damage. How to inspect yours, when to re-caulk, and what the job costs DIY vs. pro.',
    h1: 'Caulk & grout: the $30 habit that prevents $3,000 in water damage',
    updated: 'August 2026',
    readTime: 6,
    intro:
      'The most expensive water damage in most homes does not come from burst pipes — it comes from showers and tubs leaking a few drops at a time through failed caulk, for months, into the floor below. The defense costs a tube of caulk and an hour, twice a year.',
    takeaways: [
      'Water through failed bathroom caulk is a slow leak you cannot see until the subfloor is soft.',
      'Inspect twice a year: any crack, gap, peeling edge or dark staining means it is time.',
      'Re-caulking a tub or shower is $100–$300 professionally, or ~$30 in materials DIY.',
      'Outside, caulk around windows and doors is your weather seal — check it every spring.',
    ],
    sections: [
      {
        h2: 'Why such a small thing causes such big damage',
        body: [
          'Caulk is the flexible seal at every joint where tile meets tub, wall meets floor, and fixture meets surface. When it cracks or peels, shower water wicks into the gap — not enough to see, just enough to keep the wood underneath permanently damp.',
          'Damp subfloor grows mold and loses strength over months. The typical discovery moment is a soft spot in the floor, a stain on the ceiling below, or a musty smell that will not leave — and by then it is a demolition-and-rebuild job, not a caulking job.',
        ],
      },
      {
        h2: 'The twice-a-year inspection',
        body: [
          'Run your eye and a dry finger along every caulk line in the bathroom: tub-to-tile, shower corners, around the base of the toilet, sink-to-counter. You are looking for cracks, gaps, sections pulling away, and the pink-to-black staining of mildew growing behind the bead — that staining means water is already getting past.',
          'Check grout lines too: grout is porous and its sealer wears off. Water beading on grout means the sealer is working; instant darkening means it is absorbing and due for resealing.',
        ],
        tip: 'Press the tile at the bottom row of the shower wall. Any flex or hollow movement means water has been getting behind it — have it looked at before re-caulking over the problem.',
      },
      {
        h2: 'DIY or pro?',
        body: [
          'Re-caulking is one of the best-value DIY jobs in home care: strip the old bead fully, dry the joint, tape both edges, run the new bead, tool it, done. The one non-negotiable is using kitchen-and-bath (mold-resistant) caulk and letting it cure before the next shower.',
          'A pro visit at $100–$300 makes sense when the old caulk is layered and stubborn, when grout needs regrouting rather than sealing, or when that bottom-row tile flex suggests damage behind — a pro can open, assess and fix in one pass.',
        ],
      },
    ],
    faqs: [
      ['How often should bathroom caulk be replaced?', 'Inspect twice a year and expect to re-caulk high-use showers every 2–5 years. Replace at the first sign of cracking, peeling or behind-the-bead staining.'],
      ['What kind of caulk for tubs and showers?', 'A kitchen & bath silicone or siliconized acrylic labeled mold-resistant. Pure silicone lasts longest but is less forgiving to apply.'],
      ['How much does professional re-caulking cost?', 'Typically $100–$300 for a tub/shower surround in 2026 — often bundled cheaply with other small jobs in a handyman visit.'],
      ['Do I need to seal grout?', 'Cement-based grout should be sealed after installation and resealed roughly every 1–2 years in wet areas. If water soaks in instantly instead of beading, it is due.'],
    ],
    related: ['10-small-problems-big-repairs', 'home-maintenance-checklist', 'water-heater-maintenance'],
  },

  'new-homeowner-first-year': {
    service: 'New homeowner',
    category: 'Home care',
    title: 'The First-Year Homeowner’s Maintenance Guide | BestHand',
    metaDescription:
      'Just bought a house? The first-year game plan: what to locate on day one, what to service in the first month, the habits to build, and the budget to expect.',
    h1: 'The first-year homeowner’s maintenance guide',
    updated: 'August 2026',
    readTime: 8,
    intro:
      'Nobody hands you an owner’s manual with the keys. Your first year in a house is about learning its systems before any of them tests you — where the shut-offs are, what needs service now, and which habits will keep the place healthy for decades. Here is the whole first year, organized.',
    takeaways: [
      'Day one: find the main water shut-off, the electrical panel, and the gas shut-off — before you need them.',
      'Month one: change every filter, test every detector, and service whatever the inspection flagged.',
      'Budget reality: plan for roughly 1% of home value per year in maintenance and repairs.',
      'Build a simple home log — every service, repair and serial number in one place pays off for years.',
    ],
    checklists: [
      {
        title: 'Week 1 — locate and label',
        note: 'In a water or electrical emergency, minutes count. Learn these before you need them.',
        items: [
          ['Find & test the main water shut-off valve', 'Day one', 'diy'],
          ['Label the electrical panel breakers', 'Week one', 'diy'],
          ['Locate the gas meter shut-off', 'Week one', 'diy'],
          ['Find the water heater & its age sticker', 'Week one', 'diy'],
          ['Test every smoke & CO detector, replace batteries', 'Week one', 'diy'],
          ['Change the HVAC filter', 'Week one', 'diy'],
          ['Rekey or replace exterior locks', 'Week one', 'pro'],
        ],
      },
      {
        title: 'First 3 months — baseline services',
        note: 'Assume the previous owner deferred everything — start every clock at zero.',
        items: [
          ['Service the HVAC system', 'First season', 'pro'],
          ['Clean gutters & check drainage', 'First season', 'pro'],
          ['Flush the water heater & check the anode', 'First 3 months', 'pro'],
          ['Clean the dryer vent line', 'First 3 months', 'pro'],
          ['Fix everything small from the inspection report', 'First 3 months', 'pro'],
          ['Walk the roofline with binoculars for lifted shingles', 'First 3 months', 'diy'],
        ],
      },
    ],
    sections: [
      {
        h2: 'Learn the shut-offs before the emergency',
        body: [
          'The difference between a burst-hose incident and a flooded-kitchen incident is how fast someone reaches the main water valve. Find it (usually where the supply enters — basement front wall, crawl space, or near the meter), turn it once to make sure it moves, and make sure everyone in the house knows it.',
          'Same for the electrical panel — label every breaker one afternoon using a phone charger and a helper — and the gas shut-off at the meter, which takes a wrench and a quarter turn.',
        ],
      },
      {
        h2: 'Start every maintenance clock at zero',
        body: [
          'You rarely know when anything was last serviced, so assume never: HVAC tune-up, gutter cleaning, water-heater flush, dryer-vent cleaning in the first season. These first visits are also diagnostic — pros will tell you the true condition and remaining life of each system, which turns future failures into planned replacements.',
          'Your inspection report is a ready-made punch list. The "minor" items — the loose railing, the corroded valve, the cracked caulk — are exactly the small-problems-that-grow this site writes about. One handyman visit clears most of them.',
        ],
        tip: 'Photograph the data plate of every major appliance and system (model + serial) into one album. Every future service call and warranty claim starts with those numbers.',
      },
      {
        h2: 'The budget and the log',
        body: [
          'The planning rule of thumb is about 1% of home value per year for maintenance and repairs on average — lumpy in practice, some years almost nothing, then a roof. Holding that reserve is what turns failures into inconveniences instead of crises.',
          'Keep a home log — a simple note or spreadsheet: date, what was done, who did it, what it cost. It compounds: pros diagnose faster with history, warranty claims have evidence, and when you sell, a documented maintenance record is a genuine selling point.',
        ],
      },
    ],
    faqs: [
      ['What should I do first after buying a house?', 'Locate and test the water, gas and electrical shut-offs, change the locks, test all detectors, and change the HVAC filter — all in week one.'],
      ['How much should a new homeowner budget for maintenance?', 'Plan on roughly 1% of the home’s value per year for maintenance and repairs combined, held as a reserve since real costs come in lumps.'],
      ['Should I fix the small stuff from the inspection report?', 'Yes, early — those minor items are precisely the small problems that compound. Bundle them into one or two handyman visits in the first months.'],
      ['When should I service the HVAC after moving in?', 'In your first season, regardless of what the seller said — the tune-up doubles as an honest condition report on the most expensive system in the house.'],
    ],
    related: ['home-maintenance-checklist', 'fall-home-maintenance-checklist', 'how-to-hire-a-handyman'],
  },
};

export const HOME_CARE_SLUGS = Object.keys(HOME_CARE_PAGES);
